"use client"

import * as React from "react"
import { useLocation } from "react-router"

import { Kbd, KbdGroup } from "@/components/ui/kbd"
import { useAltKeyLabel, useModKeyLabel } from "@/hooks/use-mod-key-label"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"
import {
  focusAskLeoLauncher,
  focusAskLeoSurface,
  isFocusInsideAskLeo,
} from "@/lib/ask-leo-focus"
import { ASK_LEO_DOCK_KEY, type AskLeoDock } from "@/lib/ask-leo-view"
import { isEditableTarget } from "@/lib/editable-target"
import { isExamLockPath } from "@/lib/exam-lock-shell"
import {
  heroApplyUserLabel,
  heroFindingIntro,
  heroSelectedAck,
  type HeroSelectionScope,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
} from "@/lib/question-quality"
import {
  mockAssistantReply,
  useLeoThread,
  type LeoThreadMessage,
  type LeoThreadReply,
  type LeoThreadResponder,
} from "@/lib/use-leo-thread"

export type LeoThreadBridge = {
  appendExchange: (user: string, reply: LeoThreadReply) => void
  appendExchangeImmediate: (user: string, reply: LeoThreadReply) => void
  appendAssistant: (reply: LeoThreadReply) => void
  freezeCatalogAttachments: (appliedAlternateId: string) => void
  hasMessages: () => boolean
  scrollToLiveEdge: () => void
}
import { productSlug } from "@/stores/app-store"
import { useProduct } from "@/contexts/product-context"

/**
 * Page context that pages register with `useAskLeoPageContext` so Leo knows
 * what the user is currently looking at.
 */
export interface AskLeoPageContext {
  title: string
  description?: string
  /**
   * Job title in the empty-state body. Ask Leo identity in the chrome stays.
   */
  heroTitle?: string
  heroDescription?: string
  /**
   * Empty-state body under the job header. Smart enhancements puts the same
   * finding and apply cards here that the field hover already showed.
   */
  heroAttachment?: React.ReactNode
  suggestions?: string[]
  data?: Record<string, unknown>
  /**
   * The page answers its own turns while it is mounted. Set by surfaces whose
   * domain the panel cannot speak for — the question composer drafts items, and
   * a generic stub reply there would be worse than no Leo at all.
   */
  respond?: LeoThreadResponder
  /** Replaces "Ask Leo anything…" so the box says what this page expects. */
  composerPlaceholder?: string
  /** Closing line plus chips after a hero apply. */
  heroPromptBundle?: {
    lead: string
    prompts: string[]
    onSelect: (prompt: string) => void
  }
  /**
   * Seeds the first Leo turn when suggestions open with no thread yet.
   * Keeps the finding in the transcript instead of a floating hero dock.
   */
  heroSeedIntro?: string
  /**
   * The page cannot survive a Leo route change. Hide Full screen so the
   * composer (and its Smart enhancements session) stays mounted.
   */
  keepRoute?: boolean
}

interface AskLeoContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  /** Opens with the prompt typed into the composer, unsent. */
  openWithPrompt: (prompt: string) => void
  consumePendingComposerPrompt: () => string | null
  /**
   * Opens and spends the turn. For entry points that already know the whole ask
   * — the composer's Draft with Leo builds it from the form — where showing it
   * back as text to confirm is a step that answers nothing.
   */
  openWithAsk: (prompt: string) => void
  consumePendingAsk: () => string | null
  /**
   * Increments each time `openWithAsk` seeds a turn, including when Leo is
   * already open. The thread body spends the pending ask on this tick.
   */
  pendingAskTick: number
  pageContext: AskLeoPageContext | null
  setPageContext: (ctx: AskLeoPageContext | null) => void
  /**
   * Leo is composing an answer. Republished from the shell thread so entry
   * points outside the panel can show the working state instead of guessing.
   */
  busy: boolean
  setBusy: (busy: boolean) => void
  /**
   * The shell thread has at least one turn. Lives in the provider so closing
   * the panel does not wipe the session; only New chat clears it.
   */
  threadActive: boolean
  /** Panel/window conversation — shared across close, reopen, and dock switch. */
  thread: {
    messages: LeoThreadMessage[]
    isThinking: boolean
    send: ReturnType<typeof useLeoThread>["send"]
    appendExchange: ReturnType<typeof useLeoThread>["appendExchange"]
    appendExchangeImmediate: ReturnType<
      typeof useLeoThread
    >["appendExchangeImmediate"]
    appendAssistant: ReturnType<typeof useLeoThread>["appendAssistant"]
    appendAssistantLazy: ReturnType<typeof useLeoThread>["appendAssistantLazy"]
    freezeCatalogAttachments: ReturnType<
      typeof useLeoThread
    >["freezeCatalogAttachments"]
    stop: ReturnType<typeof useLeoThread>["stop"]
    reset: ReturnType<typeof useLeoThread>["reset"]
  }
  registerThreadBridge: (bridge: LeoThreadBridge | null) => void
  /** Scroll the open shell transcript so new catalog / resting foot stays in view. */
  scrollThreadToLiveEdge: () => void
  /** User turn + deferred assistant reply, then scroll the thread to the live edge. */
  appendLeoExchange: (user: string, reply: LeoThreadReply) => void
  /** User turn + pending thinking row before canvas apply runs. */
  beginHeroApply: (args: {
    check: QuestionQualityCheck
    alternate: QuestionQualityAlternate
    userLabel: string
  }) => void
  /** Locks the catalog card once canvas apply starts. */
  freezeHeroCatalog: (alternateId: string, followUps?: string[]) => void
  /** Unlocks the live catalog so the next pager row can accept a tap. */
  clearHeroCatalogFreeze: () => void
  /** Ends the pager card-body skeleton after a row finishes loading. */
  endHeroPagerLoading: () => void
  /** Resolves the pending thinking row after the field receives the change. */
  completeHeroApply: (args: {
    check: QuestionQualityCheck
    alternate: QuestionQualityAlternate
    scope: HeroSelectionScope
    reply?: LeoThreadReply
  }) => void
  /** True from hero apply click until canvas apply starts. */
  heroApplyPending: boolean
  /** True from hero apply click until the pending thread row resolves. */
  heroExchangeActive: boolean
  /** Locks the hero catalog card once canvas apply starts. */
  heroCatalogFrozenId: string | null
  /** Pager card body skeleton while the next suggestion row loads. */
  heroPagerLoading: boolean
  /** Inspector Leo spotted → Ask Leo panel: play the catalog handoff once. */
  heroQualityHandoff: boolean
  beginHeroQualityHandoff: () => void
  /**
   * Which shell renders the conversation — the docked rail or the floating
   * window. A stored preference, so it survives a reload; full screen is not
   * here because it is a route, not a preference.
   */
  dock: AskLeoDock
  setDock: (dock: AskLeoDock) => void
  /**
   * Window mode only: parked to the launcher. The window stays mounted while
   * minimised (hidden, so nothing inside it is focusable) — unmounting it would
   * throw away the conversation the user was in the middle of, which is the one
   * thing minimise must not do.
   */
  minimized: boolean
  setMinimized: (minimized: boolean) => void
}

const AskLeoContext = React.createContext<AskLeoContextValue>({
  open: false,
  setOpen: () => {},
  toggle: () => {},
  openWithPrompt: () => {},
  consumePendingComposerPrompt: () => null,
  openWithAsk: () => {},
  consumePendingAsk: () => null,
  pendingAskTick: 0,
  pageContext: null,
  setPageContext: () => {},
  busy: false,
  setBusy: () => {},
  threadActive: false,
  thread: {
    messages: [],
    isThinking: false,
    send: () => null,
    appendExchange: () => {},
    appendExchangeImmediate: () => {},
    appendAssistant: () => {},
    appendAssistantLazy: () => {},
    freezeCatalogAttachments: () => {},
    unfreezeLatestCatalogAttachment: () => {},
    stop: () => {},
    reset: () => {},
  },
  registerThreadBridge: () => {},
  scrollThreadToLiveEdge: () => {},
  appendLeoExchange: () => {},
  beginHeroApply: () => {},
  freezeHeroCatalog: () => {},
  clearHeroCatalogFreeze: () => {},
  endHeroPagerLoading: () => {},
  completeHeroApply: () => {},
  heroApplyPending: false,
  heroExchangeActive: false,
  heroCatalogFrozenId: null,
  heroPagerLoading: false,
  heroQualityHandoff: false,
  beginHeroQualityHandoff: () => {},
  dock: "panel",
  setDock: () => {},
  minimized: false,
  setMinimized: () => {},
})

function isLeoLandingPath(pathname: string, leoHref: string) {
  return pathname === leoHref || pathname.startsWith(`${leoHref}/`)
}

export function useAskLeo() {
  return React.useContext(AskLeoContext)
}

export function useAskLeoPageContext(ctx: AskLeoPageContext | null) {
  const { setPageContext } = React.useContext(AskLeoContext)
  const ctxRef = React.useRef(ctx)
  ctxRef.current = ctx
  // Sync before paint. A dep-based effect that nulls on cleanup flickers
  // heroSelection off for a frame whenever the memoized ctx object changes.
  React.useLayoutEffect(() => {
    setPageContext((current) => {
      const next = ctxRef.current
      if (!next) return current
      // Composer / import hosts own the responder; generic hub cards must not
      // stomp it when both surfaces mount in the same shell commit.
      if (!next.respond && current?.respond) return current
      if (next === current) return current
      return next
    })
  })
  React.useLayoutEffect(
    () => () => setPageContext(null),
    [setPageContext],
  )
}

export function AskLeoProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const examLock = isExamLockPath(pathname)
  const [open, setOpen] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [pageContext, setPageContext] =
    React.useState<AskLeoPageContext | null>(null)
  const pageContextRef = React.useRef(pageContext)
  pageContextRef.current = pageContext
  const shellResponder = React.useCallback<LeoThreadResponder>((prompt) => {
    const respond = pageContextRef.current?.respond
    if (respond) return respond(prompt)
    return { content: mockAssistantReply(prompt) }
  }, [])
  const {
    messages: threadMessages,
    isThinking: threadIsThinking,
    send: threadSend,
    sendAfterHeroSnapshot: threadSendAfterHeroSnapshot,
    appendExchange: threadAppendExchange,
    appendExchangeAfterHeroSnapshot: threadAppendExchangeAfterHeroSnapshot,
    appendExchangeImmediate: threadAppendExchangeImmediate,
    beginExchange: threadBeginExchange,
    completeExchange: threadCompleteExchange,
    appendAssistant: threadAppendAssistant,
    prependHeroCatalog: threadPrependHeroCatalog,
    appendAssistantLazy: threadAppendAssistantLazy,
    freezeCatalogAttachments: threadFreezeCatalogAttachments,
    unfreezeLatestCatalogAttachment: threadUnfreezeLatestCatalogAttachment,
    snapshotHeroCatalog: threadSnapshotHeroCatalog,
    stop: threadStop,
    reset: threadReset,
  } = useLeoThread({ responder: shellResponder })
  const pendingHeroExchangeRef = React.useRef<string | null>(null)
  const [heroApplyPending, setHeroApplyPending] = React.useState(false)
  const [heroExchangeActive, setHeroExchangeActive] = React.useState(false)
  const [heroCatalogFrozenId, setHeroCatalogFrozenId] = React.useState<
    string | null
  >(null)
  const [heroPagerLoading, setHeroPagerLoading] = React.useState(false)
  const [heroQualityHandoff, setHeroQualityHandoff] = React.useState(false)
  const heroQualityHandoffTimerRef = React.useRef(0)
  const threadMessagesRef = React.useRef(threadMessages)
  threadMessagesRef.current = threadMessages

  React.useEffect(() => {
    if (threadMessages.length === 0) {
      setHeroApplyPending(false)
      setHeroExchangeActive(false)
      setHeroCatalogFrozenId(null)
      setHeroPagerLoading(false)
    }
  }, [threadMessages.length])

  React.useEffect(() => {
    if (!open) {
      window.clearTimeout(heroQualityHandoffTimerRef.current)
      setHeroQualityHandoff(false)
      return
    }
    // Stale apply / exchange flags hid the catalog when reopening the panel.
    setHeroApplyPending(false)
    setHeroExchangeActive(false)
    pendingHeroExchangeRef.current = null
    setHeroCatalogFrozenId(null)
    setHeroPagerLoading(false)
  }, [open])

  const beginHeroQualityHandoff = React.useCallback(() => {
    window.clearTimeout(heroQualityHandoffTimerRef.current)
    setHeroQualityHandoff(true)
    heroQualityHandoffTimerRef.current = window.setTimeout(() => {
      setHeroQualityHandoff(false)
    }, 760)
  }, [])

  // Composer unmount clears page context. Drop any in-flight hero apply so a
  // pending row cannot leave isThinking true and stain the panel on other routes.
  React.useEffect(() => {
    if (pageContext !== null) return
    if (!heroApplyPending && !heroExchangeActive && !pendingHeroExchangeRef.current)
      return
    setHeroApplyPending(false)
    setHeroExchangeActive(false)
    setHeroCatalogFrozenId(null)
    setHeroPagerLoading(false)
    pendingHeroExchangeRef.current = null
    threadStop()
  }, [heroApplyPending, heroExchangeActive, pageContext, threadStop])

  const hadComposerHeroRef = React.useRef(false)
  React.useEffect(() => {
    const hasHero = Boolean(pageContext?.heroAttachment)
    if (hadComposerHeroRef.current && !hasHero) {
      const msgs = threadMessagesRef.current
      const hasUser = msgs.some((message) => message.role === "user")
      if (!hasUser) threadReset()
    }
    hadComposerHeroRef.current = hasHero
  }, [pageContext?.heroAttachment, threadReset])
  const threadActive = threadMessages.length > 0
  const thread = React.useMemo(
    () => ({
      messages: threadMessages,
      isThinking: threadIsThinking,
      send: threadSend,
      sendAfterHeroSnapshot: threadSendAfterHeroSnapshot,
      appendExchange: threadAppendExchange,
      appendExchangeAfterHeroSnapshot: threadAppendExchangeAfterHeroSnapshot,
      appendExchangeImmediate: threadAppendExchangeImmediate,
      beginExchange: threadBeginExchange,
      completeExchange: threadCompleteExchange,
      appendAssistant: threadAppendAssistant,
      prependHeroCatalog: threadPrependHeroCatalog,
      appendAssistantLazy: threadAppendAssistantLazy,
      freezeCatalogAttachments: threadFreezeCatalogAttachments,
      unfreezeLatestCatalogAttachment: threadUnfreezeLatestCatalogAttachment,
      snapshotHeroCatalog: threadSnapshotHeroCatalog,
      stop: threadStop,
      reset: threadReset,
    }),
    [
      threadMessages,
      threadIsThinking,
      threadSend,
      threadSendAfterHeroSnapshot,
      threadAppendExchange,
      threadAppendExchangeAfterHeroSnapshot,
      threadAppendExchangeImmediate,
      threadBeginExchange,
      threadCompleteExchange,
      threadAppendAssistant,
      threadPrependHeroCatalog,
      threadAppendAssistantLazy,
      threadFreezeCatalogAttachments,
      threadUnfreezeLatestCatalogAttachment,
      threadSnapshotHeroCatalog,
      threadStop,
      threadReset,
    ],
  )
  const [dock, setDock] = usePersistedState<AskLeoDock>(
    ASK_LEO_DOCK_KEY,
    "panel",
    {
      debounceMs: 0,
    }
  )
  // Not persisted: minimised is a "park this for a minute" state, and coming
  // back tomorrow to an invisible parked window with no conversation in it
  // would just look like Leo was broken.
  const [minimized, setMinimized] = React.useState(false)
  const toggle = React.useCallback(() => setOpen((v) => !v), [])
  const pendingComposerPromptRef = React.useRef<string | null>(null)
  /** Seeded the same way, but spent on open instead of typed into the box. */
  const pendingAskRef = React.useRef<string | null>(null)
  const [pendingAskTick, setPendingAskTick] = React.useState(0)
  const threadBridgeRef = React.useRef<LeoThreadBridge | null>(null)
  const registerThreadBridge = React.useCallback(
    (bridge: LeoThreadBridge | null) => {
      threadBridgeRef.current = bridge
    },
    [],
  )

  const scrollThreadToLiveEdge = React.useCallback(() => {
    threadBridgeRef.current?.scrollToLiveEdge()
  }, [])

  const snapshotLiveHeroCatalog = React.useCallback(() => {
    const liveAttachment = pageContextRef.current?.heroAttachment
    if (!liveAttachment) return
    const hasLiveHero = threadMessagesRef.current.some(
      (message) =>
        message.role === "assistant" &&
        message.heroCatalogLive &&
        !message.attachmentFrozen,
    )
    if (hasLiveHero) threadSnapshotHeroCatalog(liveAttachment)
  }, [threadSnapshotHeroCatalog])

  const appendLeoExchange = React.useCallback(
    (user: string, reply: LeoThreadReply) => {
      const liveAttachment = pageContextRef.current?.heroAttachment
      if (liveAttachment) {
        threadAppendExchangeAfterHeroSnapshot(user, reply, liveAttachment)
      } else {
        threadAppendExchange(user, reply)
      }
      threadBridgeRef.current?.scrollToLiveEdge()
    },
    [threadAppendExchange, threadAppendExchangeAfterHeroSnapshot],
  )

  const beginHeroApply = React.useCallback(
    (args: {
      check: QuestionQualityCheck
      alternate: QuestionQualityAlternate
      userLabel: string
    }) => {
      if (
        threadMessagesRef.current.length === 0 &&
        !pageContextRef.current?.heroAttachment
      ) {
        threadAppendAssistant({ content: heroFindingIntro(args.check) })
      }
      snapshotLiveHeroCatalog()
      setHeroCatalogFrozenId(null)
      setHeroApplyPending(true)
      setHeroExchangeActive(true)
      pendingHeroExchangeRef.current = threadBeginExchange(args.userLabel)
      requestAnimationFrame(() => {
        threadBridgeRef.current?.scrollToLiveEdge()
      })
    },
    [snapshotLiveHeroCatalog, threadAppendAssistant, threadBeginExchange],
  )

  const freezeHeroCatalog = React.useCallback(
    (alternateId: string, followUps?: string[]) => {
      setHeroApplyPending(false)
      setHeroCatalogFrozenId(alternateId)
      threadFreezeCatalogAttachments(alternateId, followUps)
      requestAnimationFrame(() => {
        threadBridgeRef.current?.scrollToLiveEdge()
      })
    },
    [threadFreezeCatalogAttachments],
  )

  const clearHeroCatalogFreeze = React.useCallback(() => {
    setHeroCatalogFrozenId(null)
  }, [])

  const endHeroPagerLoading = React.useCallback(() => {
    setHeroPagerLoading(false)
  }, [])

  const completeHeroApply = React.useCallback(
    (args: {
      check: QuestionQualityCheck
      alternate: QuestionQualityAlternate
      scope: HeroSelectionScope
      reply?: LeoThreadReply
    }) => {
      const bridge = threadBridgeRef.current
      const reply =
        args.reply ?? {
          content: heroSelectedAck(args.check, args.alternate),
        }
      const asstId = pendingHeroExchangeRef.current
      if (asstId) {
        pendingHeroExchangeRef.current = null
        threadCompleteExchange(asstId, reply)
        setHeroExchangeActive(false)
      } else {
        threadAppendExchangeImmediate(
          heroApplyUserLabel(args.check, args.alternate),
          reply,
        )
        setHeroExchangeActive(false)
      }
      endHeroPagerLoading()
    },
    [endHeroPagerLoading, threadAppendExchangeImmediate, threadCompleteExchange],
  )

  // Opening Leo always means showing Leo. Without this, a minimised window
  // would swallow the ⌘⌥K toggle and the utility-bar button.
  React.useEffect(() => {
    if (open) setMinimized(false)
  }, [open])

  const openWithPrompt = React.useCallback((prompt: string) => {
    pendingComposerPromptRef.current = prompt
    setOpen(true)
  }, [])

  const consumePendingComposerPrompt = React.useCallback(() => {
    const prompt = pendingComposerPromptRef.current
    pendingComposerPromptRef.current = null
    return prompt
  }, [])

  const openWithAsk = React.useCallback((prompt: string) => {
    snapshotLiveHeroCatalog()
    pendingAskRef.current = prompt
    setOpen(true)
    setPendingAskTick((tick) => tick + 1)
  }, [snapshotLiveHeroCatalog])

  const consumePendingAsk = React.useCallback(() => {
    const prompt = pendingAskRef.current
    pendingAskRef.current = null
    return prompt
  }, [])

  const value = React.useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      openWithPrompt,
      consumePendingComposerPrompt,
      openWithAsk,
      consumePendingAsk,
      pendingAskTick,
      pageContext,
      setPageContext,
      busy,
      setBusy,
      threadActive,
      thread,
      registerThreadBridge,
      scrollThreadToLiveEdge,
      appendLeoExchange,
      beginHeroApply,
      freezeHeroCatalog,
      clearHeroCatalogFreeze,
      endHeroPagerLoading,
      completeHeroApply,
      heroApplyPending,
      heroExchangeActive,
      heroCatalogFrozenId,
      heroPagerLoading,
      heroQualityHandoff,
      beginHeroQualityHandoff,
      dock,
      setDock,
      minimized,
      setMinimized,
    }),
    [
      open,
      toggle,
      openWithPrompt,
      consumePendingComposerPrompt,
      openWithAsk,
      consumePendingAsk,
      pendingAskTick,
      pageContext,
      busy,
      threadActive,
      thread,
      registerThreadBridge,
      scrollThreadToLiveEdge,
      appendLeoExchange,
      beginHeroApply,
      freezeHeroCatalog,
      clearHeroCatalogFreeze,
      endHeroPagerLoading,
      completeHeroApply,
      heroApplyPending,
      heroExchangeActive,
      heroCatalogFrozenId,
      heroPagerLoading,
      heroQualityHandoff,
      beginHeroQualityHandoff,
      dock,
      setDock,
      minimized,
    ]
  )

  const { product } = useProduct()
  const leoHref = `/${productSlug(product)}/leo`

  React.useEffect(() => {
    if (isLeoLandingPath(pathname, leoHref)) {
      setOpen(false)
    }
  }, [pathname, leoHref])

  const toggleRef = React.useRef(toggle)
  React.useEffect(() => {
    toggleRef.current = toggle
  })

  const openRef = React.useRef(open)
  React.useEffect(() => {
    openRef.current = open
  })

  const minimizedRef = React.useRef(minimized)
  React.useEffect(() => {
    minimizedRef.current = minimized
  })

  React.useEffect(() => {
    if (examLock) {
      setOpen(false)
      return
    }
    function onGlobalKeyDown(event: KeyboardEvent) {
      if (!event.altKey || (!event.metaKey && !event.ctrlKey)) return
      if (event.key.toLowerCase() !== "k") return
      const insideLeo = isFocusInsideAskLeo()
      // Typing in a page field means the chord belongs to that field, not to
      // us. Typing in Leo's own composer does not: the shortcut has to keep
      // working from there, or opening Leo would disarm the key that closes him.
      if (!insideLeo && isEditableTarget(event.target)) return
      event.preventDefault()
      if (openRef.current) {
        // Parked in the corner. Bring the conversation back rather than
        // toggling `open` off, which would discard the thread the user
        // minimised precisely to keep. The window moves focus on restore.
        if (minimizedRef.current) {
          setMinimized(false)
          return
        }
        // Open, but the user is working somewhere else on the page: they want
        // to get back to Leo, not dismiss him. Both shells sit at the end of
        // the tab order, so tabbing there is not a real option.
        if (!insideLeo && focusAskLeoSurface()) return
        // Focus is inside, so this press means close, and focus has to land
        // somewhere deliberate instead of collapsing to the document.
        toggleRef.current()
        focusAskLeoLauncher()
        return
      }
      toggleRef.current()
    }
    document.addEventListener("keydown", onGlobalKeyDown)
    return () => document.removeEventListener("keydown", onGlobalKeyDown)
  }, [examLock])

  return (
    <AskLeoContext.Provider value={value}>{children}</AskLeoContext.Provider>
  )
}

export function AskLeoShortcutKbds({
  className,
  variant = "tile",
}: {
  className?: string
  variant?: "tile" | "bare"
}) {
  const mod = useModKeyLabel()
  const alt = useAltKeyLabel()
  if (variant === "bare") {
    return (
      <KbdGroup className={className}>
        <Kbd variant="bare">
          {mod}
          {alt}K
        </Kbd>
      </KbdGroup>
    )
  }
  return (
    <KbdGroup className={className}>
      <Kbd>{mod}</Kbd>
      <Kbd>{alt}</Kbd>
      <Kbd>K</Kbd>
    </KbdGroup>
  )
}
